

export namespace Api {
	export type Error<Code extends string> = Readonly<{
		code: Code;
		message: string;
	}>;

	export namespace Response {
		export type Ok<T extends Record<string, unknown>> = Readonly<{
			ok: true;
			statusCode: 200;
			data: Readonly<T>;
			error: null;
		}>;
		export type BadRequest = Readonly<{
			ok: false;
			statusCode: 400;
			data: null;
			error: Api.Error<"badRequest">;
		}>;
	}
	export type Response<T extends Record<string, unknown>> = Response.Ok<T> | Response.BadRequest;

	export type SendVerificationEmailData = {
		hash: string;
	};

	export type CheckVerificationCodeData = {
		valid: boolean;
	};

	export type CreateSignUpRequestData = {
		subscribed: boolean;
	};

	export type AddressSearchResult = {
		id: string;
		attributes: Partial<{
			address: string;
			city: string;
			state: string;
			postcode: string;
			country: string;
			fullAddress: string;
		}>;
	};
	export type SearchAddressesData = {
		results: AddressSearchResult[];
		attribution: string;
	};
	export type GetAddressData = {
		id: string;
		attributes: {
			latitude: number;
			longitude: number;
		}
	};
}

type Action = (
	| "purser_send_verification_email"
	| "purser_check_verification_code"
	| "purser_create_sign_up_request"
	| "purser_search_addresses"
	| "purser_get_address"
);

type Params = Readonly<Record<string, string | number | undefined | null>>;

export class Api {
	readonly #url: string;
	constructor(url: string) {
		this.#url = url;
	}

	async #request<T extends Record<string, unknown>>(action: Action, params: Params): Promise<Api.Response<T>> {
		const search = new URLSearchParams({ action });
		for(const [ key, value ] of Object.entries(params)) {
			if(value === undefined || value === null) {
				continue;
			}
			search.set(key, value.toString());
		}

		const url = new URL(this.#url);
		url.search = search.toString();

		const response = await fetch(url);
		switch(response.status) {
			case 200:
			case 400: {
				return {
					ok: response.status === 200,
					statusCode: response.status,
					...await response.json(),
				};
			}
			default:
				throw new Error(`Failed to fetch "${action}" ${JSON.stringify(params)} : ${response.status} ${await response.text()}`);
		}
	}

	async sendVerificationEmail(params: Params): Promise<Api.Response<Api.SendVerificationEmailData>> {
		return await this.#request("purser_send_verification_email", params);
	}

	async checkVerificationCode(params: Params): Promise<Api.Response<Api.CheckVerificationCodeData>> {
		return await this.#request("purser_check_verification_code", params);
	}

	async createSignUpRequest(params: Params): Promise<Api.Response<Api.CreateSignUpRequestData>> {
		return await this.#request("purser_create_sign_up_request", params);
	}

	async searchAddresses(q: string): Promise<Api.Response<Api.SearchAddressesData>> {
		return await this.#request("purser_search_addresses", { q });
	}

	async getAddress(id: string): Promise<Api.Response<Api.GetAddressData>> {
		return await this.#request("purser_get_address", { id });
	}
}
